import { configureStore as configureStoreRTK, Store } from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import createSagaMiddleware, { SagaMiddleware } from 'redux-saga';
import { ISessionContext } from 'shared-components/authentication';
import { ApplicationState, createRootReducer, rootSaga } from '.';
import { fetchErrorsMiddleware } from '../middleware/fetch-error-middleware';
import errorService from '../services/errorService';
import isTesting from '../utilities/isTesting';

let mainTask;
export let sagaMiddleware: SagaMiddleware; // websocket saga injected async after login
export let sagaContext: ISessionContext = {
  getIdToken: () => null,
  onLogout: () => null,
  uni: undefined,
  pathway: undefined,
  isSessionValid: false,
};

// Store cleanup function for tests
export function resetStoreForTesting() {
  if (mainTask && mainTask.cancel) {
    mainTask.cancel();
    mainTask = undefined;
  }
  sagaContext = {
    getIdToken: () => null,
    onLogout: () => null,
    uni: undefined,
    pathway: undefined,
    isSessionValid: false,
  };
}

export function setSagaContext(session: ISessionContext) {
  // https://github.com/redux-saga/redux-saga/issues/2050
  return new Promise((resolve, reject) => {
    try {
      mainTask.setContext({ sagaContext: { ...session } });
      resolve(true);
    } catch (err) {
      reject('Could not load context');
    }
  });
}

const sentryReduxEnhancer = createReduxEnhancer({
  // filter redux actions here
});

export default function configureStore(): Store<ApplicationState> {
  sagaMiddleware = createSagaMiddleware({
    onError: error => {
      errorService.catch(error);
    },
    context: {
      sagaContext: null,
    },
  });
  const middlewares = [sagaMiddleware, fetchErrorsMiddleware];

  const store = configureStoreRTK({
    reducer: createRootReducer(),
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: false,
        serializableCheck: false,
        immutableCheck: false,
      }).concat(...middlewares),
    enhancers: [sentryReduxEnhancer],
    devTools: {
      name: 'CAS Shield: Students',
    },
  });
  if (window.Cypress && isTesting) {
    (window as any).__store__ = store;

    // Preserve existing globalConfig values (e.g., from Cypress e2e.js) and merge with defaults
    const existingConfig = (window as any).globalConfig || {};
    // @ts-ignore - Property 'globalConfig' does not exist on type 'Window & typeof globalThis'
    (window as any).globalConfig = {
      apiUrl: 'https://dev-api.cas-staff.net',
      assetsUrl: 'https://dev-customer-artifacts.s3.eu-west-2.amazonaws.com',
      cognitoDomain: '',
      cognitoProviderName: '',
      idpLogout: false,
      myInterviewAPIKey:
        'apikey 8bz12e7yfpw5pokqhalrzyne sig=8aed5921084046ed95eb042f44edfe1582cc1436f75b559ca16d7dac89ddb62d',
      redirectUrl: '',
      samlEnabled: false,
      staffAppClient: '1j8icosoasn54v6ss95dgtp8va',
      staffMFAEnabled: false,
      studentAppClient: '1j8icosoasn54v6ss95dgtp8va',
      studentsMFAEnabled: false,
      uni: 'ADM',
      wssUrl: 'wss://ul3yqz8boh.execute-api.eu-west-2.amazonaws.com/prod',
      userpool: 'eu-west-2_iWT5Zbnsv',
      // Merge with existing config - allows Cypress to override values like apiUrl
      ...existingConfig,
    };
  }

  mainTask = sagaMiddleware.run(rootSaga);
  return store;
}
